import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { NotaryDetailPage } from "@/pages/notaries/notary-detail-page";
import { AccountantDetailPage } from "@/pages/accountants/accountant-detail-page";
import { listProfessionals } from "@/services/professionalsService";

/**
 * One detail route, and the panel is chosen by VERIFICATION STRATEGY
 * (spec 2f0e5645; the same call decision a78bb4f made for acceptance).
 *
 * WHY STRATEGY AND NOT SLUG. The accountant detail is not a variant of the
 * notary detail — it is the `document_lookup` detail, carrying a certificate
 * and a reviewer's decision that the register-verified flow has no concept
 * of. Keying on the slug would mean a second poder_judicial profession needed
 * a third page to show it the same thing; keying on the strategy means it
 * needs none, which is the claim this whole programme rests on.
 *
 * NOT MERGED INTO ONE PAGE WITH CONDITIONALS, deliberately. The two are 461
 * and 429 lines and differ by a whole review flow, exactly as the two
 * acceptance pages do — and those were not merged either. Conditionals would
 * reproduce `discipline === 'notary' ? … : …` inside a screen, which is the
 * shape profession-copy.ts was written to delete.
 *
 * ITS SIBLING is PAGE_BY_STRATEGY in pages/auth/professional-invite-route.tsx,
 * which picks the ACCEPTANCE page for the same strategies. Deliberately two
 * maps rather than one shared registry — admin and acceptance are different
 * surfaces and coupling them would make a change to one a change to both —
 * but a strategy added to either almost always wants an entry in the other,
 * so they name each other here and there.
 */
const DETAIL_BY_STRATEGY: Record<string, () => JSX.Element> = {
  poder_judicial: NotaryDetailPage,
  document_lookup: AccountantDetailPage,
};

export function ProfessionalDetailRoute(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  /*
    The list is the cheapest place to learn a professional's strategy: it is
    already served with every row, and the admin arrives here from it, so the
    query is usually a cache hit rather than a round trip.
  */
  const query = useQuery({
    queryKey: ["platform", "professionals"],
    queryFn: () => listProfessionals(),
  });

  if (query.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-8">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  const professional = (query.data ?? []).find((p) => p.id === id);
  const Page = professional?.verificationStrategy
    ? DETAIL_BY_STRATEGY[professional.verificationStrategy]
    : undefined;

  if (Page) return <Page />;

  /*
    A STRATEGY WITH NO PANEL IS SAID OUT LOUD, not redirected. A professional
    registered under a strategy this build has no screen for is a real person
    an admin is looking for; bouncing them to the list would read as "no such
    professional", which is the one thing that is not true. Same reasoning as
    the acceptance dispatcher's unregistered-strategy card.
  */
  return (
    <div className="p-4 md:p-8">
      <Card className="w-full shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("professionalsSection.noPanelTitle")}
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {t("professionalsSection.noPanelBody", {
              profession:
                professional?.professionDisplayName ??
                professional?.professionSlug ??
                "—",
            })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
